import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, BadgeCheck, CalendarCheck, Pencil, Plus, ShieldQuestion, Wallet } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { EmptyState, Money, PageHeader, TableSkeleton } from "../components/erp";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";

const EMPTY_EMP = { name: "", mobile: "", email: "", father_name: "", dob: "", gender: "", blood_group: "",
  address: "", department: "", designation: "", joining_date: "", salary: 0, bank_name: "", account_no: "", ifsc: "", status: "active" };
const KYC_LABEL = { pending: "Pending", submitted: "Submitted", under_verification: "Under Verification", verified: "Verified", rejected: "Rejected" };
const KYC_COLOR = { pending: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-400", submitted: "bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-400", under_verification: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-400", verified: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-400", rejected: "bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-400" };

function EmployeeDialog({ open, onClose, emp, onSaved }) {
  const [form, setForm] = useState(EMPTY_EMP);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(emp ? { ...EMPTY_EMP, ...emp } : EMPTY_EMP); }, [emp, open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, salary: +form.salary || 0 };
      if (emp) await api.put(`/employees/${emp.id}`, payload);
      else await api.post("/employees", payload);
      toast.success(emp ? "Employee updated" : "Employee created");
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900" data-testid="employee-dialog">
        <DialogHeader><DialogTitle className="font-display">{emp ? "Edit Employee" : "New Employee"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Full Name *</Label><Input data-testid="emp-name" className="mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
          <div><Label>Mobile</Label><Input className="mt-1.5" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} /></div>
          <div><Label>Email</Label><Input type="email" className="mt-1.5" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
          <div><Label>Date of Birth</Label><Input type="date" className="mt-1.5" value={form.dob} onChange={(e) => set("dob", e.target.value)} /></div>
          <div><Label>Department</Label><Input data-testid="emp-department" className="mt-1.5" value={form.department} onChange={(e) => set("department", e.target.value)} /></div>
          <div><Label>Designation</Label><Input className="mt-1.5" value={form.designation} onChange={(e) => set("designation", e.target.value)} /></div>
          <div><Label>Joining Date</Label><Input type="date" className="mt-1.5" value={form.joining_date} onChange={(e) => set("joining_date", e.target.value)} /></div>
          <div><Label>Monthly Salary (₹)</Label><Input data-testid="emp-salary" type="number" min="0" className="mt-1.5" value={form.salary} onChange={(e) => set("salary", e.target.value)} /></div>
          <div><Label>Bank Name</Label><Input className="mt-1.5" value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} /></div>
          <div><Label>Account No / IFSC</Label>
            <div className="flex gap-2 mt-1.5">
              <Input value={form.account_no} onChange={(e) => set("account_no", e.target.value)} />
              <Input className="font-mono w-32" value={form.ifsc} onChange={(e) => set("ifsc", e.target.value.toUpperCase())} />
            </div>
          </div>
          <div className="col-span-2"><Label>Address</Label><Input className="mt-1.5" value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="emp-save-btn" onClick={save} disabled={busy} className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">{busy ? "Saving..." : "Save Employee"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmployeesTab() {
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState({ open: false, emp: null });
  const { activeId } = useCompany();
  const load = () => api.get("/employees?limit=300").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (activeId) { setData(null); load(); } }, [activeId]); // eslint-disable-line

  const setKyc = async (emp, status) => {
    try {
      await api.post(`/employees/${emp.id}/kyc`, { status });
      toast.success(`KYC for ${emp.name} → ${KYC_LABEL[status]}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <Button data-testid="new-employee-btn" onClick={() => setDialog({ open: true, emp: null })}
          className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><Plus className="mr-1.5 h-4 w-4" /> New Employee</Button>
      </div>
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        {!data ? <TableSkeleton /> : data.items.length === 0 ? (
          <EmptyState testId="employees-empty" title="No employees yet" body="Add your team — KYC, attendance and payroll plug in automatically." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="employees-table">
              <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="p-3">Code</th><th className="p-3">Name</th><th className="p-3">Department</th>
                <th className="p-3">Designation</th><th className="p-3">Mobile</th><th className="p-3 text-right">Salary</th>
                <th className="p-3">KYC</th><th className="p-3"></th></tr></thead>
              <tbody>
                {data.items.map((e) => (
                  <tr key={e.id} data-testid={`emp-row-${e.emp_code}`} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                    <td className="p-3 font-mono text-xs text-slate-500">{e.emp_code}</td>
                    <td className="p-3 font-medium">{e.name}</td>
                    <td className="p-3 text-slate-500">{e.department || "—"}</td>
                    <td className="p-3 text-slate-500">{e.designation || "—"}</td>
                    <td className="p-3 text-slate-500">{e.mobile || "—"}</td>
                    <td className="p-3 text-right"><Money value={e.salary} /></td>
                    <td className="p-3"><Badge className={`${KYC_COLOR[e.kyc_status]} border-0 text-[10px]`} data-testid={`kyc-badge-${e.emp_code}`}>{KYC_LABEL[e.kyc_status]}</Badge></td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" data-testid={`emp-edit-${e.emp_code}`} onClick={() => setDialog({ open: true, emp: e })}><Pencil className="h-3.5 w-3.5" /></Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="ghost" data-testid={`kyc-menu-${e.emp_code}`}><ShieldQuestion className="h-3.5 w-3.5" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white dark:bg-slate-900">
                            {["submitted", "under_verification", "verified", "rejected"].map((s) => (
                              <DropdownMenuItem key={s} data-testid={`kyc-set-${s}-${e.emp_code}`} onClick={() => setKyc(e, s)} className="cursor-pointer">
                                <BadgeCheck className="mr-2 h-3.5 w-3.5" /> {KYC_LABEL[s]}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <EmployeeDialog open={dialog.open} emp={dialog.emp} onClose={() => setDialog({ open: false, emp: null })} onSaved={load} />
    </div>
  );
}

function AttendanceTab() {
  const { activeId } = useCompany();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ employee_id: "", date: new Date().toISOString().slice(0, 10), status: "present", check_in: "", check_out: "", note: "" });

  const load = () => {
    api.get(`/attendance?month=${month}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/employees?limit=300").then((r) => setEmployees(r.data.items)).catch(() => {});
  };
  useEffect(() => { if (activeId) { setData(null); load(); } }, [activeId, month]); // eslint-disable-line

  const mark = async () => {
    if (!form.employee_id) { toast.error("Select an employee"); return; }
    try {
      await api.post("/attendance/mark", form);
      toast.success("Attendance marked");
      setDialog(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <Input data-testid="attendance-month" type="month" className="w-44" value={month} onChange={(e) => setMonth(e.target.value)} />
        <Button data-testid="mark-attendance-btn" onClick={() => setDialog(true)}
          className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><CalendarCheck className="mr-1.5 h-4 w-4" /> Mark Attendance</Button>
      </div>
      {data && data.summary.length > 0 && (
        <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="attendance-summary">
          {data.summary.map((s) => (
            <div key={s.employee_id} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
              <p className="font-medium text-sm truncate">{s.name}</p>
              <p className="text-xs text-slate-400 font-mono">{s.code}</p>
              <div className="mt-2 flex gap-3 text-xs font-mono">
                <span className="text-emerald-600" title="Present">P:{s.present}</span>
                <span className="text-red-500" title="Absent">A:{s.absent}</span>
                <span className="text-amber-500" title="Half day">H:{s.half_day}</span>
                <span className="text-sky-500" title="Leave">L:{s.leave}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        {!data ? <TableSkeleton /> : data.items.length === 0 ? (
          <EmptyState testId="attendance-empty" title="No attendance marked" body={`Mark attendance for ${month} to build the register.`} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="attendance-table">
              <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="p-3">Date</th><th className="p-3">Employee</th><th className="p-3">Status</th>
                <th className="p-3">In</th><th className="p-3">Out</th><th className="p-3">Note</th></tr></thead>
              <tbody>
                {data.items.map((a) => (
                  <tr key={a.id} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="p-3 font-mono text-xs">{a.date}</td>
                    <td className="p-3">{a.employee_name}</td>
                    <td className="p-3">
                      <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${
                        { present: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-400",
                          absent: "bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-400",
                          half_day: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-400",
                          leave: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-400",
                          holiday: "bg-slate-100 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300" }[a.status]}`}
                        data-testid={`att-status-${a.date}-${a.employee_id}`}>
                        {a.status.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="p-3 font-mono text-xs">{a.check_in || "—"}</td>
                    <td className="p-3 font-mono text-xs">{a.check_out || "—"}</td>
                    <td className="p-3 text-slate-500 text-xs">{a.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent aria-describedby={undefined} className="max-w-md bg-white dark:bg-slate-900" data-testid="attendance-dialog">
          <DialogHeader><DialogTitle className="font-display">Mark Attendance</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Employee *</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm((f) => ({ ...f, employee_id: v }))}>
                <SelectTrigger data-testid="attendance-employee" className="mt-1.5"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} ({e.emp_code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input data-testid="attendance-date" type="date" className="mt-1.5" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} /></div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="attendance-status" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{["present", "absent", "half_day", "leave", "holiday"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Check In</Label><Input type="time" className="mt-1.5" value={form.check_in} onChange={(e) => setForm((f) => ({ ...f, check_in: e.target.value }))} /></div>
            <div><Label>Check Out</Label><Input type="time" className="mt-1.5" value={form.check_out} onChange={(e) => setForm((f) => ({ ...f, check_out: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button data-testid="attendance-save-btn" onClick={mark} className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">Mark</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PayrollTab() {
  const { activeId } = useCompany();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/payroll?month=${month}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (activeId) { setData(null); load(); } }, [activeId, month]); // eslint-disable-line

  const generate = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/payroll/generate", { month });
      toast.success(r.message);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <Input data-testid="payroll-month" type="month" className="w-44" value={month} onChange={(e) => setMonth(e.target.value)} />
        <Button data-testid="generate-payroll-btn" onClick={generate} disabled={busy}
          className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><Wallet className="mr-1.5 h-4 w-4" /> {busy ? "Generating..." : `Generate Payroll for ${month}`}</Button>
      </div>
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        {!data ? <TableSkeleton /> : data.items.length === 0 ? (
          <EmptyState testId="payroll-empty" title="No payroll generated" body="Generate payroll — salary is prorated by attendance when marked." />
        ) : (
          <>
            <div className="border-b border-slate-100 dark:border-slate-800 p-4 text-sm">
              Total payable: <Money value={data.total_payable} className="font-bold" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payroll-table">
                <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="p-3">Employee</th><th className="p-3">Department</th><th className="p-3 text-right">Basic</th>
                  <th className="p-3 text-right">Payable Days</th><th className="p-3 text-right">Net Payable</th><th className="p-3">Basis</th></tr></thead>
                <tbody>
                  {data.items.map((p) => (
                    <tr key={p.id} data-testid={`payroll-row-${p.emp_code}`} className="border-b border-slate-100 dark:border-slate-800">
                      <td className="p-3"><p className="font-medium">{p.employee_name}</p><p className="text-xs text-slate-400 font-mono">{p.emp_code}</p></td>
                      <td className="p-3 text-slate-500">{p.department || "—"}</td>
                      <td className="p-3 text-right"><Money value={p.basic} /></td>
                      <td className="p-3 text-right font-mono">{p.payable_units}/{p.days_in_month}</td>
                      <td className="p-3 text-right"><Money value={p.payable} className="font-bold text-emerald-600" /></td>
                      <td className="p-3 text-xs text-slate-500">{p.basis}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function HR() {
  return (
    <div data-testid="hr-page">
      <PageHeader title="HR & Payroll" testId="hr-title" sub="Employees, KYC, attendance and salary" />
      <Tabs defaultValue="employees">
        <TabsList className="mb-4">
          <TabsTrigger value="employees" data-testid="tab-employees">Employees & KYC</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll">Payroll</TabsTrigger>
        </TabsList>
        <TabsContent value="employees"><EmployeesTab /></TabsContent>
        <TabsContent value="attendance"><AttendanceTab /></TabsContent>
        <TabsContent value="payroll"><PayrollTab /></TabsContent>
      </Tabs>
    </div>
  );
}
